import { FunctionComponent, useState, useEffect, useCallback, useRef } from "react";
import "./ChatHistoryHeader.scss";
import { Button } from "@panviva/panviva-react-ui";
import { Popover } from "@panviva/panviva-react-ui";
import { SK_CHAT_HISTORY_HEADER_CLASS } from "../../../constants/sidekick";
import FilterPanel from "../filterPanel/FilterPanel";
import { useSelector } from "react-redux";
import { selectChatHistory } from "../../../features/sidekick/sidekickSlice";

interface ChatHistoryHeaderProps {
    disableFilterButton?: boolean;
    onFilterLookupFailure?: () => void;
}

export const ChatHistoryHeader: FunctionComponent<ChatHistoryHeaderProps> = ({ disableFilterButton = false, onFilterLookupFailure }) => {

    const [filterOpen, setFilterOpen] = useState(false);
    const [filterApplied, setFilterApplied] = useState(false);
    const filterButtonContainerRef = useRef<HTMLDivElement>(null);
    const shouldRestoreFocusRef = useRef(false);
    const chatHistoryState = useSelector(selectChatHistory) ?? { filterParams: {} };

    const focusFilterButton = useCallback(() => {
        try {
            const button = filterButtonContainerRef.current?.querySelector('button') as HTMLButtonElement | null;
            button?.focus();
        } catch {
            // Ignore focus restoration failures to avoid breaking filter close flow.
        }
    }, []);

    // Sync local state with Redux filter state
    useEffect(() => {
        const hasFilters = Object.keys(chatHistoryState.filterParams || {}).length > 0;
        setFilterApplied(hasFilters);
    }, [chatHistoryState.filterParams]);

    const handleFilterClick = (value: boolean) => {
        if (!value) {
            shouldRestoreFocusRef.current = true;
        }
        setFilterOpen(value);
    }

    const handleFilterClose = () => handleFilterClick(false);

    useEffect(() => {
        // Focus restoration is performed after React commits the closed state,
        // avoiding timeout-based ordering assumptions.
        if (!filterOpen && shouldRestoreFocusRef.current) {
            shouldRestoreFocusRef.current = false;
            focusFilterButton();
        }
    }, [filterOpen, focusFilterButton]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (!event.defaultPrevented && event.key === "Escape" && filterOpen) {
                event.preventDefault();
                event.stopPropagation();
                handleFilterClose();
            }
        };
        document.addEventListener("keydown", handleEscapeKey);
        return () => document.removeEventListener("keydown", handleEscapeKey);
    }, [filterOpen]);

    useEffect(() => {
        if (disableFilterButton) {
            setFilterOpen(false);
        }
    }, [disableFilterButton]);

    return (
        <div className={SK_CHAT_HISTORY_HEADER_CLASS} data-testid="sk-chat-history-header">
            <h1>Sidekick Chat History & Feedback</h1>
            <div className="filter-container">
                <div ref={filterButtonContainerRef}>
                    <Button
                        variant="outlined"
                        icon={filterApplied ? "filter-active" : "filter-inactive"}
                        onClick={() => handleFilterClick(!filterOpen)}
                        disabled={disableFilterButton}
                        aria-haspopup="dialog"
                        aria-expanded={filterOpen}
                    >
                        Filter
                    </Button>
                </div>
                <Popover open={filterOpen} className="filter-popover" onClose={(handleFilterClose)}>
                    <FilterPanel onClose={handleFilterClick} onLookupFailure={onFilterLookupFailure} />
                </Popover>
            </div>
        </div>
    );
}